import { useContext, useState } from 'react'
import { useNavigate } from 'react-router'
import Hashids from 'hashids'
import { IconButton, Tooltip } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import StopIcon from '@mui/icons-material/Stop'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import DeleteIcon from '@mui/icons-material/Delete'
import PlaybookEditModal from '../../Modals/PlaybookUpdateModal'
import ConfirmationModal from '../../Modals/ConfirmationModal'
import PlaybookRunModal from '../../Modals/PlaybookRunModal'
import { useToast } from '../../../../contexts/toast.context'
import { PlaybookContext } from '../../../../contexts/playbooks.context'
import { deletePlaybook } from '../../../../services/playbook.service'
import { IPlaybook } from '../../../../types/playbook.type'

import styles from './styles.module.scss'
import { formatDate } from '../../../../utils/dateFormatter'
import { DataGrid } from '@mui/x-data-grid'

export const hashids = new Hashids(process.env.REACT_APP_HASHID_SECRET, 10)

const PlaybookTable = () => {
  const [modalType, setModalType] = useState('')
  const [selectedPlaybook, setSelectedPlaybook] = useState<
    IPlaybook | undefined
  >()

  const {
    playbooks,
    paginationModel,
    setPaginationModel,
    refreshList,
    total,
    isLoading,
  } = useContext(PlaybookContext)
  const navigate = useNavigate()
  const { showToast } = useToast()

  const handleClick = (
    evt: React.MouseEvent,
    playbook: IPlaybook,
    type: 'edit' | 'run' | 'delete',
  ) => {
    evt.stopPropagation()
    setSelectedPlaybook(playbook)
    setModalType(type)
  }

  const handleDelete = async () => {
    if (selectedPlaybook) {
      const data = await deletePlaybook(selectedPlaybook.id)

      if (data.success) {
        handleClose()
        refreshList()
        showToast('success', data.message)
      }
    }
  }

  const handleNavigate = (id: number) => {
    navigate(`/${hashids.encode(id)}`)
  }

  const handleClose = () => {
    setModalType('')
    setSelectedPlaybook(undefined)
  }

  return (
    <>
      <DataGrid
        loading={isLoading}
        rowSelection={false}
        onRowClick={(e) => handleNavigate(e.row.id)}
        getRowClassName={() => styles.datagridrow}
        //density='compact'
        columns={[
          {
            field: 'id',
            headerName: 'ID',
            width: 70,
            sortable: false,
            filterable: false,
          },
          {
            field: 'name',
            headerName: 'Name',
            flex: 1.5,
            sortable: false,
            filterable: false,
          },
          {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            sortable: false,
            filterable: false,
          },
          {
            field: 'created_at',
            headerName: 'Created at',
            flex: 1,
            sortable: false,
            filterable: false,
            valueFormatter: formatDate,
          },
          {
            field: 'functions',
            headerName: ' ',
            disableColumnMenu: true,
            sortable: false,
            width: 170,
            align: 'right',
            renderCell: (p) => {
              return (
                <>
                  <Tooltip
                    title={
                      p.row.status === 'running'
                        ? 'Stop the playbook'
                        : 'Start the playbook'
                    }
                    disableInteractive
                  >
                    <IconButton
                      className={styles.action}
                      onClick={(evt) => handleClick(evt, p.row, 'run')}
                    >
                      {p.row.status === 'running' ? (
                        <StopIcon />
                      ) : (
                        <PlayArrowIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit the playbook" disableInteractive>
                    <IconButton
                      className={styles.action}
                      onClick={(evt) => handleClick(evt, p.row, 'edit')}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete the playbook" disableInteractive>
                    <IconButton
                      id="deleteButton"
                      className={styles.action}
                      onClick={(evt) => handleClick(evt, p.row, 'delete')}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )
            },
          },
        ]}
        rows={playbooks}
        rowCount={total}
        pagination
        paginationMode="server"
        paginationModel={paginationModel}
        pageSizeOptions={[20, 50, 100]}
        onPaginationModelChange={setPaginationModel}
      />

      {modalType === 'delete' && (
        <ConfirmationModal
          text="Are you sure you want to delete playbook?"
          btnText="Delete"
          btnIcon={<DeleteIcon />}
          onConfirm={handleDelete}
          onClose={handleClose}
        />
      )}

      {modalType === 'run' && (
        <PlaybookRunModal
          isOpen={true}
          playbook={selectedPlaybook}
          onClose={handleClose}
        />
      )}

      {modalType === 'edit' && selectedPlaybook && (
        <PlaybookEditModal playbook={selectedPlaybook} onClose={handleClose} />
      )}
    </>
  )
}

export default PlaybookTable
